import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ClipSpinner } from '~/components/ui';
import {
  comparedRequestRowElementId,
  requestForQuoteOwnViewLink,
} from '~/utils/requestForQuoteLinks';
import { QuoteComparisonSection } from '~/pages/rfq-view/sections/quote-comparison/QuoteComparisonSection';
import type { BuyListQuoteComparisons } from './useBuyListQuoteComparisons';
import { ComparedRequestRows } from './ComparedRequestRows';

type BuyListQuoteComparisonProps = {
  comparisons: BuyListQuoteComparisons;
  focusedRequestForQuoteId?: string | null;
};

export function BuyListQuoteComparison({
  comparisons: {
    isLoading,
    error,
    comparisons,
    negotiationByRequestForQuoteId,
    loadedRequestForQuoteIds,
    shownRequestsForQuote,
    totalRequestsForQuote,
  },
  focusedRequestForQuoteId = null,
}: Readonly<BuyListQuoteComparisonProps>) {
  const { t } = useTranslation('inventory');
  const isFocusedRequestLoaded =
    focusedRequestForQuoteId !== null &&
    loadedRequestForQuoteIds.has(focusedRequestForQuoteId);

  useEffect(() => {
    if (!isFocusedRequestLoaded) return;
    const focusedRequestRow = document.getElementById(
      comparedRequestRowElementId(focusedRequestForQuoteId)
    );
    if (focusedRequestRow === null) return;
    focusedRequestRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [isFocusedRequestLoaded, focusedRequestForQuoteId]);

  if (isLoading) {
    return <ClipSpinner />;
  }
  if (error) {
    return (
      <div>
        {t('inventory.buy_list.requests_sent.quote_comparison.loading_error', {
          message: error.message,
        })}
      </div>
    );
  }

  const pricingModes = new Set(
    comparisons.map(({ requestForQuote }) => requestForQuote.useTallyPrice)
  );
  const hasMixedPricing = pricingModes.size > 1;

  return (
    <QuoteComparisonSection
      comparisons={comparisons}
      negotiationByRequestForQuoteId={negotiationByRequestForQuoteId}
      renderGroupHeader={(comparison) => (
        <ComparedRequestRows requestsForQuote={comparison.requestsForQuote} />
      )}
      notices={
        <ComparisonNotices
          unloadedFocusedRequestForQuoteId={
            focusedRequestForQuoteId !== null && !isFocusedRequestLoaded
              ? focusedRequestForQuoteId
              : null
          }
          shownRequestsForQuote={shownRequestsForQuote}
          totalRequestsForQuote={totalRequestsForQuote}
          hasMixedPricing={hasMixedPricing}
        />
      }
      emptyMessage={t(
        'inventory.buy_list.requests_sent.quote_comparison.empty'
      )}
      testId="buy-list-comparison"
    />
  );
}

function ComparisonNotices({
  unloadedFocusedRequestForQuoteId,
  shownRequestsForQuote,
  totalRequestsForQuote,
  hasMixedPricing,
}: Readonly<{
  unloadedFocusedRequestForQuoteId: string | null;
  shownRequestsForQuote: number;
  totalRequestsForQuote: number;
  hasMixedPricing: boolean;
}>) {
  const { t } = useTranslation('inventory');

  return (
    <>
      {unloadedFocusedRequestForQuoteId !== null && (
        <FocusedRequestNotLoadedNotice
          requestForQuoteId={unloadedFocusedRequestForQuoteId}
        />
      )}
      {totalRequestsForQuote > shownRequestsForQuote && (
        <p
          className="text-sm text-neut-sec"
          data-testid="buy-list-comparison-truncated"
        >
          {t('inventory.buy_list.requests_sent.quote_comparison.truncated', {
            shown: shownRequestsForQuote,
            total: totalRequestsForQuote,
          })}
        </p>
      )}
      {hasMixedPricing && (
        <p
          className="text-sm text-neut-sec"
          data-testid="buy-list-comparison-mixed-pricing"
        >
          {t(
            'inventory.buy_list.requests_sent.quote_comparison.mixed_pricing_notice'
          )}
        </p>
      )}
    </>
  );
}

function FocusedRequestNotLoadedNotice({
  requestForQuoteId,
}: Readonly<{ requestForQuoteId: string }>) {
  const { t } = useTranslation('rfq-view');

  return (
    <p
      className="text-sm text-neut-sec"
      data-testid="buy-list-comparison-focused-request-not-loaded"
    >
      {t('rfq_view.quote_comparison.focused_request_not_loaded')}{' '}
      <Link
        to={requestForQuoteOwnViewLink(requestForQuoteId)}
        className="font-medium text-prim underline"
      >
        {t('rfq_view.quote_comparison.open_focused_request')}
      </Link>
    </p>
  );
}
